/**
 * Helper function that casts the mix constant value to a boolean
 */
const envToBoolean = (value?: string): boolean => {
  return value === 'true' || value === '1';
};

/**
 * Algolia
 */
export const ALGOLIA_APP_ID = process.env.MIX_ALGOLIA_APP_ID;
export const ALGOLIA_API_SEARCH_KEY = process.env.MIX_ALGOLIA_ANONYMOUS_USER_SEARCH_KEY;
export const ALGOLIA_SORT_BY_LOCATION_IF_SET = false;

/**
 * Algolia Indexes
 */
export const ALGOLIA_RECENT_INDEX_SUFFIX = process.env.MIX_ALGOLIA_RECENT_INDEX_SUFFIX;
export const ALGOLIA_BREEDER_INDEX = process.env.MIX_ALGOLIA_BREEDER_INDEX_NAME;
export const ALGOLIA_BREED_INDEX = process.env.MIX_ALGOLIA_BREED_INDEX_NAME;
export const ALGOLIA_SELLER_CONTACTS_INDEX = process.env.MIX_ALGOLIA_SELLER_CONTACTS_INDEX_NAME;
export const ALGOLIA_LITTERS_INDEX = process.env.MIX_ALGOLIA_LITTERS_INDEX_NAME;
export const ALGOLIA_ALL_LISTINGS_INDEX = process.env.MIX_ALGOLIA_ALL_LISTINGS_INDEX_NAME;
export const ALGOLIA_ALL_LISTINGS_INDEX_RECENT = `${ALGOLIA_ALL_LISTINGS_INDEX}_${ALGOLIA_RECENT_INDEX_SUFFIX}`;
export const ALGOLIA_ALL_CONTENT_INDEX = process.env.MIX_ALGOLIA_ALL_CONTENT_INDEX_NAME;
export const ALGOLIA_USERS_INDEX = process.env.MIX_ALGOLIA_USERS_INDEX_NAME;
export const ALGOLIA_ORDERS_INDEX = process.env.MIX_ALGOLIA_ORDERS_INDEX_NAME;
export const ALGOLIA_MESSAGES_INDEX = process.env.MIX_ALGOLIA_MESSAGES_INDEX_NAME;
export const ALGOLIA_FORM_SUBMISSIONS_INDEX = process.env.MIX_ALGOLIA_ALL_FORMS_INDEX_NAME;

/**
 * CLOUDINARY CLOUD NAMES
 */
export const CLOUDINARY_CLOUD_NAME = process.env.MIX_CLOUDINARY_CLOUD_NAME;
export const CLOUDINARY_UPLOAD_SOURCES = ['local', 'camera'];

/**
 * CLOUDINARY UPLOAD PRESETS
 */
/* eslint-disable max-len */
export const CLOUDINARY_UPLOAD_PRESET_DEFAULT = process.env.MIX_CLOUDINARY_UPLOAD_PRESET_DEFAULT;
export const CLOUDINARY_UPLOAD_PRESET_LISTING = process.env.MIX_CLOUDINARY_UPLOAD_PRESET_LISTING;
export const CLOUDINARY_UPLOAD_PRESET_PUPPY = process.env.MIX_CLOUDINARY_UPLOAD_PRESET_PUPPY;
export const CLOUDINARY_UPLOAD_PRESET_SELLER = process.env.MIX_CLOUDINARY_UPLOAD_PRESET_SELLER;
export const CLOUDINARY_UPLOAD_PRESET_SELLER_LOGO = process.env.MIX_CLOUDINARY_UPLOAD_PRESET_SELLER_LOGO;
export const CLOUDINARY_UPLOAD_PRESET_BREED = process.env.MIX_CLOUDINARY_UPLOAD_PRESET_BREED;
export const CLOUDINARY_UPLOAD_PRESET_FORM_SUBMISSION = process.env.MIX_CLOUDINARY_UPLOAD_PRESET_FORM_SUBMISSION;
export const CLOUDINARY_UPLOAD_PRESET_TESTIMONIAL = process.env.MIX_CLOUDINARY_UPLOAD_PRESET_TESTIMONIAL;
export const CLOUDINARY_UPLOAD_PRESET_BANNER = process.env.MIX_CLOUDINARY_UPLOAD_PRESET_BANNER;
export const CLOUDINARY_UPLOAD_PRESET_CONTENT = process.env.MIX_CLOUDINARY_UPLOAD_PRESET_CONTENT;
/* eslint-enable max-len */

export type CloudinaryUploadPreset =
  typeof CLOUDINARY_UPLOAD_PRESET_DEFAULT |
  typeof CLOUDINARY_UPLOAD_PRESET_LISTING |
  typeof CLOUDINARY_UPLOAD_PRESET_PUPPY |
  typeof CLOUDINARY_UPLOAD_PRESET_SELLER |
  typeof CLOUDINARY_UPLOAD_PRESET_BREED |
  typeof CLOUDINARY_UPLOAD_PRESET_FORM_SUBMISSION |
  typeof CLOUDINARY_UPLOAD_PRESET_TESTIMONIAL |
  typeof CLOUDINARY_UPLOAD_PRESET_BANNER |
  typeof CLOUDINARY_UPLOAD_PRESET_CONTENT;

/**
 * Routes
 * NOTE: These should match the routes found in the `routes/` dir
 */

export const BROWSER_API_VERSION = 'v2';
export const BROWSER_API_PREFIX = `/api/browser/${BROWSER_API_VERSION}/`;
export const WEB_PREFIX = '/';
export const ADMIN_PREFIX = `${WEB_PREFIX}admin/`;
export const ASSET_PREFIX = WEB_PREFIX;

// Pages
export const PAGES_PREFIX = `${WEB_PREFIX}pages`;
export const PAGES_ADMIN_PREFIX = `${ADMIN_PREFIX}pages`;

// Blogs
export const BLOGS_PREFIX = `${WEB_PREFIX}blogs`;
export const BLOGS_ADMIN_PREFIX = `${ADMIN_PREFIX}blogs`;

// Breeds
export const BREEDS_ADMIN_PREFIX = `${ADMIN_PREFIX}breeds`;
export const BREEDS_BROWSER_API_PREFIX = `${BROWSER_API_PREFIX}breeds`;

// Ads
export const ADS_ADMIN_PREFIX = `${ADMIN_PREFIX}ads`;
export const ADS_ADMIN_PUPPY_PREFIX = `${ADMIN_PREFIX}ads/puppy`;
export const ADS_ADMIN_STUD_PREFIX = `${ADMIN_PREFIX}ads/stud`;
export const PUPPY_ADMIN_PREFIX = `${ADMIN_PREFIX}puppy`;
export const REPORTS_ADMIN_PREFIX = `${ADMIN_PREFIX}reports`;

// Litters
export const LITTERS_ADMIN_PREFIX = `${ADMIN_PREFIX}litters`;

// Sellers
export const SELLER_PREFIX = `${WEB_PREFIX}sellers/`;
export const PROFILES_ADMIN_PREFIX = `${ADMIN_PREFIX}seller-profiles`;
export const PROFILES_ADMIN_CREATION_SUFFIX = `${WEB_PREFIX}seller-profiles/create`;
export const PROFILES_BROWSER_API_PREFIX = `${BROWSER_API_PREFIX}seller-profiles`;

// Users
export const USER_ADMIN_PREFIX = `${ADMIN_PREFIX}users`;
export const USER_ADMIN_EDIT_SUFFIX = '/edit';

// SellerContacts
export const SELLER_CONTACT_ADMIN_PREFIX = `${ADMIN_PREFIX}seller-contacts`;

// Orders
export const ORDER_ADMIN_PREFIX = `${ADMIN_PREFIX}orders`;
export const ORDER_ADMIN_VIEW_PREFIX = `${ADMIN_PREFIX}resources/orders`;

// Forms
export const FORM_SUBMISSIONS_ADMIN_PREFIX = `${ADMIN_PREFIX}form-submissions`;
export const FORM_SUBMISSIONS_SELLER_REVIEWS_ADMIN_PREFIX = `${FORM_SUBMISSIONS_ADMIN_PREFIX}/seller-review`;

export const FORM_SUBMISSIONS_BROWSER_API_PREFIX = `${BROWSER_API_PREFIX}form-submissions`;

export const FORM_SUBMISSIONS_REPORTS_BROWSER_API_PREFIX = `${FORM_SUBMISSIONS_BROWSER_API_PREFIX}/report`;

// Messages
export const MESSAGES_ADMIN_PREFIX = `${ADMIN_PREFIX}all-messages`;
export const MESSAGES_BROWSER_API_PREFIX = `${BROWSER_API_PREFIX}messages`;
export const MESSAGE_THREAD_BROWSER_API_PREFIX = `${BROWSER_API_PREFIX}message-thread`;

// Seller Reviews
export const FORM_SUBMISSIONS_SELLER_REVIEWS_BROWSER_API_PREFIX = `${FORM_SUBMISSIONS_BROWSER_API_PREFIX}/seller-review`;
export const SELLER_REVIEWS_SHOW_SUFFIX = (sellerId: string): string => `${FORM_SUBMISSIONS_SELLER_REVIEWS_BROWSER_API_PREFIX}/${sellerId}`;

// Testimonials
export const FORM_SUBMISSIONS_TESTIMONIALS_BROWSER_API_PREFIX = `${FORM_SUBMISSIONS_BROWSER_API_PREFIX}/testimonial`;

/**
 * Google
 */
export const GOOGLE_MAPS_API_KEY = process.env.MIX_GOOGLE_MAPS_API_KEY;
export const GOOGLE_DEVELOPER_KEY = process.env.MIX_GOOGLE_DEVELOPER_KEY;
export const GOOGLE_RECAPTCHA_KEY = process.env.MIX_GOOGLE_RECAPTCHA_KEY;

/**
 * Breed Constants
 */
export const BREED_SIZE_VALUES = {
  Toy: 'toy',
  Teacup: 'teacup',
  Mini: 'mini',
  Extra: 'extra small',
  Small: 'small',
  Medium: 'medium',
  Large: 'large',
  'Extra Large': 'extra large',
  All: 'all',
} as const;

// pedigree constants
export const BREED_PEDIGREE_TYPES = {
  Designer: 'designer',
  Purebred: 'purebred',
} as const;

export const COAT_TYPES = {
  Silky: 'silky',
  Double: 'double',
  Wire: 'wire',
  Curly: 'curly',
  Smooth: 'smooth',
  Rough: 'rough',
  Wavy: 'wavy',
  Hairless: 'hairless',
  Corded: 'corded',
  All: 'all',
} as const;

export const COAT_LENGTHS = {
  Short: 'short',
  Medium: 'medium',
  Long: 'long',
  All: 'all',
} as const;

export const ENERGY_LEVELS = {
  'Very High': 'very high',
  High: 'high',
  Medium: 'medium',
  Low: 'low',
} as const;

export const SHEDDING_LEVELS = {
  Light: 'light',
  Moderate: 'moderate',
  Heavy: 'heavy',
} as const;

/**
 * Listing Constants
 */
export const PUPPY_TYPE_NAME = 'puppy';
export const STUD_TYPE_NAME = 'stud';

/**
 * Image Constants
 */
export const PUPPY_DEFAULT_IMAGE = `${ASSET_PREFIX}assets/default_puppy.jpg`;
export const BLOG_DEFAULT_IMAGE = `${ASSET_PREFIX}assets/bg_mingle.png`;
export const BREED_DEFAULT_IMAGE = `${ASSET_PREFIX}assets/bg_mingle.png`;
export const LITTER_DEFAULT_IMAGE = `${ASSET_PREFIX}assets/default_puppy.jpg`;
export const BREEDER_SELLER_DEFAULT_IMAGE = `${ASSET_PREFIX}assets/user-icon-default.svg`;
export const BREEDER_SELLER_DEFAULT_IMAGE_OWP = `${ASSET_PREFIX}assets/user_owp.svg`;
export const LISTING_BOOSTED_BADGE_IMAGE = `${ASSET_PREFIX}assets/boost_badge.svg`;
export const BOOST_ICON_IMAGE = `${ASSET_PREFIX}assets/boost_icon.svg`;
export const CAMERA_ICON_IMAGE = `${ASSET_PREFIX}assets/camera_icon.svg`;

/**
 * Seller Validation Constants
 */
export const SELLER_MAX_PROFILE_DESCRIPTION_LENGTH = 10000;

/**
 * Password Rules
 */
export const PASSWORD_MIN_LENGTH = Number(process.env.MIX_PASSWORD_MIN_LENGTH);
export const PASSWORD_MAX_LENGTH = Number(process.env.MIX_PASSWORD_MAX_LENGTH);
export const PASSWORD_INCLUDE_LOWERCASE = envToBoolean(process.env.MIX_PASSWORD_INCLUDE_LOWERCASE);
export const PASSWORD_INCLUDE_UPPERCASE = envToBoolean(process.env.MIX_PASSWORD_INCLUDE_UPPERCASE);
export const PASSWORD_INCLUDE_NUMBERS = envToBoolean(process.env.MIX_PASSWORD_INCLUDE_NUMBERS);
export const PASSWORD_INCLUDE_SYMBOLS = envToBoolean(process.env.MIX_PASSWORD_INCLUDE_SYMBOLS);

/**
 * Impersonation
 */
export const IMPERSONATION_PREFIX = `${ADMIN_PREFIX}impersonate`;
export const LEAVE_IMPERSONATION = `${ADMIN_PREFIX}impersonate/leave`;

// Define searchable attributes on a per-search level below

/**
 * These are the attributes in Algolia searchableAttributes for the `*_all_listings` indices that
 * are searchable publicly. For instance, a search for a User account phone number should NOT cause
 * that User's listings to show as results, so the `user_phone` field is not included here
 */
export const publiclySearchableAllListingsAttributes = [
  'ad_type',
  'admin_breed_parents',
  'age',
  'birth_date',
  'breed_id',
  'breed',
  'category',
  'city',
  'colors',
  'contact.alternate_phone_alternate_formats',
  'contact.alternate_phone_extn',
  'contact.alternate_phone',
  'contact.best_time_to_call',
  'contact.phone_alternate_formats',
  'contact.phone',
  'description',
  'external_breeder_id',
  'generation',
  'global_hit_title',
  'id',
  'listings_type',
  'name',
  'price',
  'priceGroup',
  'real_price',
  'registrations',
  'sale_price',
  'seller_business_name',
  'seller_name',
  'sex',
  'size',
  'state',
  'user_email',
  'user_name',
];

/**
 * These are the searchableAttributes that can be searched over for the public Breed search
 */
export const publiclySearchableBreedAttributes = ['name'];

/**
 * List of attributes to be used as search refinements for listings on the front-end
 */
export const listingSearchRefinements = [
  'admin_breed_parents',
  'admin_single_breed',
  'category',
  'colors',
  'generation',
  'registrations',
  'sex',
  'size',
  'state',
  'city',
];

/**
 * List of attributes to be used as search refinements for breeders on the front-end
 */
export const breederSearchRefinements = [
  'breed',
  'state',
];

/**
 * List of age selection options to use in select-dropdown boxes for Algolia search.
 * Age uses a timestamp query via `birth_date`, but we want to use a dropdown with
 * preselected, human-readable options.
 */
export const validAgeSelectionOptions = [
  {
    value: '3',
    label: 'Less than three months',
  },
  {
    value: '6',
    label: 'Less than six months',
  },
  {
    value: '9',
    label: 'Less than nine months',
  },
  {
    value: '12',
    label: 'Less than a year',
  },
  {
    value: '1',
    label: 'More than a year',
  },
];

/**
 * List of attributes to be returned as properties of the `hit` objects returned with
 * Algolia results for listings in the SearchSuggestions component.
 */
export const listingAttributesToRetrieveSearchSuggestions = [
  'global_hit_title',
  'route',
];

/**
 * List of attributes to be returned as properties of the `hit._highlightResult` objects returned
 * with Algolia results for listings in the SearchSuggestions component.
 */
export const listingAttributesToHighlightSearchSuggestions = [
  'global_hit_title',
];

/**
 * List of attributes to be returned as properties of `hit` objects returned with Algolia results
 * for listings in the SearchResultsBlock component and displayed in the PuppyCard component.
 */
export const listingAttributesToRetrievePuppyCard = [
  '_geoloc',
  'ad_type',
  'admin_breed_display',
  'availability',
  'birthDate',
  'breed_name',
  'city',
  'date_available',
  'description',
  'generation',
  'has_video',
  'id',
  'listingType',
  'name',
  'objectID',
  'price',
  'real_price',
  'registrations',
  'route',
  'sale_price',
  'sex',
  'size',
  'state',
  'thumbnailUrl',
  'verified',
  'zip',
  'is_boosted',
  'created_at',
  'photo_gallery',
  'sellerBusinessName',
  'seller_profile_photo',
  'seller_average_rating',
];

/**
 * List of attributes to be returned as properties of the `hit._highlightResult` objects returned
 * with Algolia results for listings in the SearchResultsBlock component and displayed in the
 * PuppyCard component.
 */
export const listingAttributesToHighlightPuppyCard = [];

/**
 * List of attributes to be returned as properties of `hit` objects returned with Algolia results
 * for listings in the SearchResultsBlock component and displayed in the BreederCard component.
 */
export const listingAttributesToRetrieveBreederCard = [
  '_geoloc',
  'breed_name',
  'city',
  'id',
  'name',
  'objectID',
  'profile_photo_url',
  'route',
  'state',
  'tier',
];

/**
 * List of attributes to be returned as properties of the `hit._highlightResult` objects returned
 * with Algolia results for listings in the SearchResultsBlock component and displayed in the
 * BreederCard component.
 */
export const listingAttributesToHighlightBreederCard = [];

/**
 * Precision settings to be used for Algolia queries where location should impact search rankings,
 * but not via a strict sort by location.
 * cf. https://www.algolia.com/doc/api-reference/api-parameters/aroundPrecision/
 */
export const algoliaPrecisionSettings = 400000;
